import { useMenuStyles } from '@chakra-ui/icons'
import React, { use, createContext, KeyboardEvent, MouseEvent, useCallback, useState } from 'react'
import {
  asLink,
  useMenuDescendantsContext,
  Box,
  BoxProps,
  forwardRef,
  Menu,
  MenuButtonProps,
  MenuItem,
  MenuItemProps,
  MenuList,
  MenuListProps,
  MenuProps,
  useMenuButton,
  useMenuContext,
  UsePopperProps
} from '~/design_system'

type ParentMenuContext = ReturnType<typeof useMenuContext> & {
  blurAndSaveFocusedIndex: () => void
  restoreLastFocusedIndex: () => void
}
const ParentMenuContext = createContext<undefined | ParentMenuContext>(undefined)
export const useParentMenuContext = () => use(ParentMenuContext)

export const HoverMenu = ({ children, ...props }: MenuProps & { isDisabled?: boolean }) => {
  const parentMenu = useMenuContext()
  const [lastFocusedIndex, setLastFocusedIndex] = useState(-1)
  function blurAndSaveFocusedIndex() {
    setLastFocusedIndex(parentMenu.focusedIndex)
    parentMenu?.setFocusedIndex(-1)
  }
  function restoreLastFocusedIndex() {
    parentMenu?.setFocusedIndex(lastFocusedIndex)
  }

  const subMenuPosition: UsePopperProps = { offset: [-8, -4], placement: 'right-start' }
  const parentMenuPosition: UsePopperProps = { offset: [0, 0], placement: 'bottom-start' }
  const menuPosition = parentMenu ? subMenuPosition : parentMenuPosition

  return (
    <ParentMenuContext
      value={{
        ...parentMenu,
        blurAndSaveFocusedIndex,
        restoreLastFocusedIndex
      }}>
      <Menu preventOverflow {...menuPosition} {...props}>
        {(...args) => (
          <HoverMenuInner isDisabled={props.isDisabled}>
            {typeof children === 'function' ? children(...args) : children}
          </HoverMenuInner>
        )}
      </Menu>
    </ParentMenuContext>
  )
}

function HoverMenuInner({ isDisabled, ...props }: BoxProps & { isDisabled?: boolean }) {
  const menu = useMenuContext()
  return (
    <Box
      {...props}
      {...(!isDisabled && {
        onMouseEnter: menu.openAndFocusMenu,
        onMouseLeave: menu.onClose
      })}
    />
  )
}

export const SubMenuButton = forwardRef<MenuButtonProps, 'button'>((props, ref) => {
  const menu = useMenuContext()
  const parentMenu = useParentMenuContext()
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const isSupportedOpeningKey = ['Enter', 'ArrowLeft', ' '].includes(normalizeEventKey(event))
      if (isSupportedOpeningKey) {
        event.preventDefault()
        event.stopPropagation()
        parentMenu?.blurAndSaveFocusedIndex()
        menu.onOpen()
        setTimeout(() => {
          menu.setFocusedIndex(1)
        }, 10)
      }
    },
    [parentMenu, menu]
  )

  function onClick(event: MouseEvent) {
    event.stopPropagation()
  }

  const buttonProps = useMenuButton(props, ref)
  const styles = useMenuStyles()

  return <MenuItem sx={styles.button} {...props} {...buttonProps} onClick={onClick} onKeyDownCapture={onKeyDown} />
})

SubMenuButton.displayName = 'SubMenuButton'

export const SubMenuList = forwardRef<MenuListProps, 'button'>((props, ref) => {
  const subMenu = useMenuContext()
  const descendants = useMenuDescendantsContext()
  const parentMenu = useParentMenuContext()
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const key = normalizeEventKey(event)
      const isSupportedClosingKey = ['Escape', 'ArrowRight'].includes(key)
      if (isSupportedClosingKey) {
        event.preventDefault()
        event.stopPropagation()
        subMenu.onClose()
        setTimeout(() => {
          parentMenu?.restoreLastFocusedIndex()
        }, 100)
        return
      }

      if (key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        let nextIndex = getNextIndex(subMenu.focusedIndex, descendants.count())
        if (nextIndex === 0) nextIndex = 1
        subMenu.setFocusedIndex(nextIndex)
      }

      if (key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        let prevIndex = getPrevIndex(subMenu.focusedIndex, descendants.count())
        if (prevIndex === 0) prevIndex = descendants.count() - 1
        subMenu.setFocusedIndex(prevIndex)
      }
    },
    [parentMenu, subMenu, descendants]
  )

  return <MenuList ref={ref} {...props} onKeyDown={onKeyDown} />
})

SubMenuList.displayName = 'SubMenuList'

export type MenuItemLinkProps = MenuItemProps & { isHidden?: boolean; href?: string }
export const MenuItemLink = forwardRef<MenuItemLinkProps, 'a'>(({ isHidden, href, ...props }, ref) => {
  if (isHidden) return null

  // to me this is weird.  If a menu item is disabled we should probably just hide it?
  if (props.isDisabled) {
    return <MenuItem ref={ref} {...props} />
  }
  return <MenuItem {...asLink(href)} ref={ref} {...props} />
})

MenuItemLink.displayName = 'MenuItemLink'

/**
 * Get the normalized event key across all browsers
 * @param event keyboard event
 */
export function normalizeEventKey(event: Pick<KeyboardEvent, 'key' | 'keyCode'>) {
  const { key, keyCode } = event

  const isArrowKey = keyCode >= 37 && keyCode <= 40 && key.indexOf('Arrow') !== 0

  const eventKey = isArrowKey ? `Arrow${key}` : key

  return eventKey as EventKeys
}

export type EventKeys =
  | 'ArrowDown'
  | 'ArrowUp'
  | 'ArrowLeft'
  | 'ArrowRight'
  | 'Enter'
  | 'Space'
  | 'Tab'
  | 'Backspace'
  | 'Control'
  | 'Meta'
  | 'Home'
  | 'End'
  | 'PageDown'
  | 'PageUp'
  | 'Delete'
  | 'Escape'
  | ' '
  | 'Shift'

export function getNextIndex(current: number, max: number, loop: boolean = true) {
  let next = current + 1
  if (loop && next >= max) next = 0
  return next
}

export function getPrevIndex(current: number, max: number, loop: boolean = true) {
  let next = current - 1
  if (loop && next < 0) next = max
  return next
}
